import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  parseAiDeckDifficulty,
  parseResponseMode,
  type AiDeckDifficulty,
  type ResponseMode,
  type Seat,
} from '@shared/index'
import { DEFAULT_MAT_ID, DEFAULT_MAT_OPACITY, parseMatId, parseMatOpacity } from '../game/gamemats'
import { DEFAULT_SLEEVE_ID, parseSleeveId } from '../game/sleeves'

export type VsAiFirst = 'you' | 'cpu'
export type CardArtRegion = 'ocg' | 'tcg'
/** How much of the duel presentation this device draws. */
export type MotionLevel = 'full' | 'reduced' | 'minimal'
/** Board foil sweep. `full` is the usual shine. `light` shares one texture. */
export type FoilSweep = 'full' | 'light'
/**
 * How sharp card faces and ATK/DEF are on this device. `sharp` draws those
 * in the page, over the 1024×768 canvas. `standard` keeps the lighter board
 * sprites. The canvas size does not change.
 */
export type BoardSharpness = 'sharp' | 'standard'
/** Coin dialog. `high` is the 3D toss. `low` is the flat CSS spin. */
export type CoinQuality = 'high' | 'low'
export type { AiDeckDifficulty, ResponseMode }

export const MOTION_LEVEL_ORDER: MotionLevel[] = ['full', 'reduced', 'minimal']
export const FOIL_SWEEP_ORDER: FoilSweep[] = ['full', 'light']
export const BOARD_SHARPNESS_ORDER: BoardSharpness[] = ['sharp', 'standard']
export const COIN_QUALITY_ORDER: CoinQuality[] = ['high', 'low']

export function parseFoilSweep(value: unknown): FoilSweep {
  return value === 'light' ? 'light' : 'full'
}

export function parseBoardSharpness(value: unknown): BoardSharpness {
  return value === 'standard' ? 'standard' : 'sharp'
}

export function parseMotionLevel(value: unknown): MotionLevel {
  if (value === 'reduced' || value === 'minimal') return value
  return 'full'
}

export function parseCoinQuality(value: unknown): CoinQuality {
  return value === 'low' ? 'low' : 'high'
}

/**
 * Old saves stored a boolean `coinToss`. Off becomes Low once. A later
 * High pick still sticks.
 */
const COIN_QUALITY_FLAG = 'dc-coin-quality-v1'

function readLegacyCoinTossOff(): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    const raw = localStorage.getItem('duelPrefs')
    if (!raw) return false
    const data = JSON.parse(raw) as { coinToss?: unknown }
    return data.coinToss === false
  } catch {
    return false
  }
}

function migrateCoinQuality(stored: unknown): CoinQuality {
  const parsed = parseCoinQuality(stored)
  if (typeof localStorage === 'undefined') {
    return readLegacyCoinTossOff() ? 'low' : parsed
  }
  if (localStorage.getItem(COIN_QUALITY_FLAG)) return parsed
  localStorage.setItem(COIN_QUALITY_FLAG, '1')
  if (readLegacyCoinTossOff()) return 'low'
  return parsed
}

/** Drop the old boolean so a later load does not treat Off as missing and pick High. */
function writeCoinQuality(level: CoinQuality): void {
  if (typeof localStorage === 'undefined') return
  try {
    const raw = localStorage.getItem('duelPrefs')
    const data = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
    data.coinQuality = level
    delete data.coinToss
    localStorage.setItem('duelPrefs', JSON.stringify(data))
  } catch {
    /* the next settings change rewrites the blob */
  }
}

function applyCoinQualityMigration(store: {
  coinQuality: CoinQuality
  setCoinQuality: (level: CoinQuality) => void
  $persist?: () => void
}): void {
  const hadFlag =
    typeof localStorage !== 'undefined' && localStorage.getItem(COIN_QUALITY_FLAG) != null
  const level = migrateCoinQuality(store.coinQuality)
  store.setCoinQuality(level)
  if (hadFlag) return
  writeCoinQuality(level)
  store.$persist?.()
}

/**
 * Old saves stored a boolean. Fold `true` into Reduced once; a later pick
 * (including Full) still sticks.
 */
const MOTION_LEVEL_FLAG = 'dc-motion-level-v1'

function migrateMotionLevel(stored: unknown, legacyReduced: boolean): MotionLevel {
  const parsed = parseMotionLevel(stored)
  if (typeof localStorage === 'undefined') {
    return legacyReduced && parsed === 'full' ? 'reduced' : parsed
  }
  if (localStorage.getItem(MOTION_LEVEL_FLAG)) return parsed
  localStorage.setItem(MOTION_LEVEL_FLAG, '1')
  if (legacyReduced) return 'reduced'
  return parsed
}

function readLegacyReduced(): boolean {
  if (typeof localStorage === 'undefined') return false
  try {
    const raw = localStorage.getItem('duelPrefs')
    if (!raw) return false
    const data = JSON.parse(raw) as { reduceAnimations?: unknown }
    return data.reduceAnimations === true
  } catch {
    return false
  }
}

function applyMotionMigration(store: {
  motionLevel: MotionLevel
  reduceAnimations: boolean
  setMotionLevel: (level: MotionLevel) => void
  $persist?: () => void
}): void {
  const hadFlag =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem(MOTION_LEVEL_FLAG) != null
  const legacyReduced = store.reduceAnimations === true || readLegacyReduced()
  store.reduceAnimations = false
  store.setMotionLevel(migrateMotionLevel(store.motionLevel, legacyReduced))
  // Hydration finishes before the persist subscription exists, so write the
  // folded level now. Otherwise a refresh would see the flag and the old blob.
  if (!hadFlag) store.$persist?.()
}

export const RESPONSE_MODE_ORDER: ResponseMode[] = ['always', 'auto', 'minimal', 'off']

export function parseCardArtRegion(value: unknown): CardArtRegion {
  return value === 'ocg' ? 'ocg' : 'tcg'
}

/**
 * Old default was OCG and Pinia wrote it on first visit, so it looks like a
 * choice. Flip once to TCG; later OCG picks in Settings still stick.
 */
const CARD_ART_DEFAULT_TCG_FLAG = 'dc-card-art-default-tcg-v1'

/**
 * Old default was auto and Pinia wrote it on first visit, so it looks like a
 * choice. Flip once to minimal; a later Auto pick in Settings still sticks.
 */
const RESPONSE_MODE_DEFAULT_MINIMAL_FLAG = 'dc-response-mode-default-minimal-v1'

function migrateResponseModeDefault(stored: unknown): ResponseMode {
  const parsed = parseResponseMode(stored)
  if (typeof localStorage === 'undefined') return parsed === 'auto' ? 'minimal' : parsed
  if (localStorage.getItem(RESPONSE_MODE_DEFAULT_MINIMAL_FLAG)) return parsed
  localStorage.setItem(RESPONSE_MODE_DEFAULT_MINIMAL_FLAG, '1')
  return parsed === 'auto' ? 'minimal' : parsed
}

function migrateCardArtDefaultToTcg(stored: unknown): CardArtRegion {
  const parsed = parseCardArtRegion(stored)
  if (typeof localStorage === 'undefined') return parsed
  if (localStorage.getItem(CARD_ART_DEFAULT_TCG_FLAG)) return parsed
  localStorage.setItem(CARD_ART_DEFAULT_TCG_FLAG, '1')
  return 'tcg'
}

/**
 * A phone or a phone-shaped window. A desktop mouse does not match, even
 * if the laptop also has a touch screen.
 */
export function phonePlaysLightGraphics(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(pointer: coarse)').matches) return true
  if (window.matchMedia('(hover: none)').matches) return true
  if (window.matchMedia('(max-width: 720px)').matches) return true
  return window.matchMedia('(max-height: 500px) and (max-width: 1100px)').matches
}

/**
 * Phones used to start on Standard image quality and Light foil. That pair
 * was automatic. Lift it back to the maximum once. A later choice sticks.
 */
const GRAPHICS_PHONE_MAX_FLAG = 'dc-graphics-phone-max-v1'

function liftAutoPhoneGraphics(store: {
  boardSharpness: BoardSharpness
  foilSweep: FoilSweep
  setBoardSharpness: (level: BoardSharpness) => void
  setFoilSweep: (mode: FoilSweep) => void
  $persist?: () => void
}): void {
  if (typeof localStorage === 'undefined') return
  if (localStorage.getItem(GRAPHICS_PHONE_MAX_FLAG)) return
  localStorage.setItem(GRAPHICS_PHONE_MAX_FLAG, '1')
  if (!phonePlaysLightGraphics()) return
  if (store.boardSharpness !== 'standard' || store.foilSweep !== 'light') return
  store.setBoardSharpness('sharp')
  store.setFoilSweep('full')
  store.$persist?.()
}

export const useDuelPrefsStore = defineStore(
  'duelPrefs',
  () => {
    /** When true, vs-AI duels open the dice overlay. */
    const vsAiDice = ref(true)
    /** Used only when vsAiDice is false. */
    const vsAiFirst = ref<VsAiFirst>('you')
    /** CPU deck recipe and hidden-info vision for local vs-AI. */
    const vsAiDifficulty = ref<AiDeckDifficulty>('normal')
    /** Catalog id or `random`. Covers only the local player's field half. */
    const matId = ref(DEFAULT_MAT_ID)
    /** 0–100: how strong the playmat art is. */
    const matOpacity = ref(DEFAULT_MAT_OPACITY)
    /**
     * When true, summons / activations / attacks from the opponent fill the
     * card viewer (desktop rail and compact split).
     */
    const followOpponentViewer = ref(true)
    /**
     * Phone landscape: zoom the Phaser camera onto hand / your field / opponent.
     * Desktop is unchanged. Default on.
     */
    const focusBands = ref(true)
    /**
     * Compact duel: hide the right-hand card text panel. Lists (Monster Reborn,
     * Big Eye) always hide it while they are open. Not synced to the account.
     */
    const hideCardViewer = ref(false)
    /** Notes that offer extra coins for playing ranked. */
    const rankedPrompts = ref(true)
    /** Catalog id or `random`. Face-down backs for the local player. */
    const sleeveId = ref(DEFAULT_SLEEVE_ID)
    /**
     * Illustration on the card face. Default `tcg` (international art).
     * `ocg` is the YGOPRODeck scan (English text, often the Japanese picture).
     */
    const cardArtRegion = ref<CardArtRegion>('tcg')
    /**
     * How eager trap-response prompts are during a duel. Sent to the engine
     * as SET_RESPONSE_MODE. Default minimal.
     */
    const responseMode = ref<ResponseMode>('minimal')
    /**
     * Presentation cost on this device. `reduced` skips particles, shake, and
     * field-spell motion. `minimal` also skips 3D flights, the field backdrop,
     * and animated foil. Not synced to the account.
     */
    const motionLevel = ref<MotionLevel>('full')
    /**
     * How the board draws foil shine. `full` is the usual sweep. `light`
     * shares one texture so a phone can hold a deck of foil cards. Not synced.
     */
    const foilSweep = ref<FoilSweep>('full')
    /**
     * Card faces, sleeves, and ATK/DEF. `sharp` draws them in the page.
     * `standard` keeps the lighter board sprites. The canvas stays 1024×768.
     * Read when the duel board is created. Not synced to the account.
     * Every device starts on `sharp`. A later pick sticks.
     */
    const boardSharpness = ref<BoardSharpness>('sharp')
    /**
     * When true, album and list thumbs run the foil sweep. Off, they keep a
     * still tint and only while near the screen. Not synced to the account.
     */
    const animateFoilThumbs = ref(false)
    /**
     * Coin dialog quality. High opens the 3D toss. Low uses the flat CSS
     * spin and no extra WebGL context. Not synced. An old Off save lands
     * on Low.
     */
    const coinQuality = ref<CoinQuality>('high')
    /**
     * Legacy boolean from before `motionLevel`. Hydrated from old saves so
     * the one-time migration can see it. Not written back.
     */
    const reduceAnimations = ref(false)

    function setVsAiDice(on: boolean): void {
      vsAiDice.value = on
    }

    function setVsAiFirst(who: VsAiFirst): void {
      vsAiFirst.value = who === 'cpu' ? 'cpu' : 'you'
    }

    function setVsAiDifficulty(level: AiDeckDifficulty): void {
      vsAiDifficulty.value = parseAiDeckDifficulty(level)
    }

    function setMatId(id: string): void {
      matId.value = parseMatId(id)
    }

    function setMatOpacity(value: number): void {
      matOpacity.value = parseMatOpacity(value)
    }

    function setFollowOpponentViewer(on: boolean): void {
      followOpponentViewer.value = on
    }

    function setFocusBands(on: boolean): void {
      focusBands.value = on
    }

    function setHideCardViewer(on: boolean): void {
      hideCardViewer.value = on === true
    }

    function setRankedPrompts(on: boolean): void {
      rankedPrompts.value = on
    }

    function setSleeveId(id: string): void {
      sleeveId.value = parseSleeveId(id)
    }

    function setCardArtRegion(region: CardArtRegion): void {
      cardArtRegion.value = parseCardArtRegion(region)
    }

    function setResponseMode(mode: ResponseMode): void {
      responseMode.value = parseResponseMode(mode)
    }

    function setMotionLevel(level: MotionLevel): void {
      motionLevel.value = parseMotionLevel(level)
    }

    function setFoilSweep(mode: FoilSweep): void {
      foilSweep.value = parseFoilSweep(mode)
    }

    function setBoardSharpness(level: BoardSharpness): void {
      boardSharpness.value = parseBoardSharpness(level)
    }

    function setAnimateFoilThumbs(on: boolean): void {
      animateFoilThumbs.value = on === true
    }

    function setCoinQuality(level: CoinQuality): void {
      coinQuality.value = parseCoinQuality(level)
    }

    function cycleResponseMode(): void {
      const i = RESPONSE_MODE_ORDER.indexOf(responseMode.value)
      responseMode.value = RESPONSE_MODE_ORDER[(i + 1) % RESPONSE_MODE_ORDER.length]!
    }

    /** Seat 0 is the human in LocalEngineClient. */
    function localFirstSeat(): Seat {
      return vsAiFirst.value === 'cpu' ? 1 : 0
    }

    return {
      vsAiDice,
      vsAiFirst,
      vsAiDifficulty,
      matId,
      matOpacity,
      followOpponentViewer,
      focusBands,
      hideCardViewer,
      rankedPrompts,
      sleeveId,
      cardArtRegion,
      responseMode,
      motionLevel,
      foilSweep,
      boardSharpness,
      animateFoilThumbs,
      coinQuality,
      reduceAnimations,
      setVsAiDice,
      setVsAiFirst,
      setVsAiDifficulty,
      setMatId,
      setMatOpacity,
      setFollowOpponentViewer,
      setFocusBands,
      setHideCardViewer,
      setRankedPrompts,
      setSleeveId,
      setCardArtRegion,
      setResponseMode,
      setMotionLevel,
      setFoilSweep,
      setBoardSharpness,
      setAnimateFoilThumbs,
      setCoinQuality,
      cycleResponseMode,
      localFirstSeat,
    }
  },
  {
    persist: {
      paths: [
        'vsAiDice',
        'vsAiFirst',
        'vsAiDifficulty',
        'matId',
        'matOpacity',
        'followOpponentViewer',
        'focusBands',
        'hideCardViewer',
        'rankedPrompts',
        'sleeveId',
        'cardArtRegion',
        'responseMode',
        'motionLevel',
        'foilSweep',
        'boardSharpness',
        'animateFoilThumbs',
        'coinQuality',
      ],
      afterRestore: (ctx) => {
        ctx.store.setMatId(parseMatId(ctx.store.matId))
        ctx.store.setMatOpacity(parseMatOpacity(ctx.store.matOpacity))
        ctx.store.setFollowOpponentViewer(ctx.store.followOpponentViewer !== false)
        ctx.store.setFocusBands(ctx.store.focusBands !== false)
        ctx.store.setHideCardViewer(ctx.store.hideCardViewer === true)
        ctx.store.setRankedPrompts(ctx.store.rankedPrompts !== false)
        ctx.store.setSleeveId(parseSleeveId(ctx.store.sleeveId))
        ctx.store.setCardArtRegion(migrateCardArtDefaultToTcg(ctx.store.cardArtRegion))
        ctx.store.setVsAiDifficulty(parseAiDeckDifficulty(ctx.store.vsAiDifficulty))
        ctx.store.setResponseMode(migrateResponseModeDefault(ctx.store.responseMode))
        ctx.store.setFoilSweep(parseFoilSweep(ctx.store.foilSweep))
        ctx.store.setBoardSharpness(parseBoardSharpness(ctx.store.boardSharpness))
        liftAutoPhoneGraphics(
          ctx.store as unknown as {
            boardSharpness: BoardSharpness
            foilSweep: FoilSweep
            setBoardSharpness: (level: BoardSharpness) => void
            setFoilSweep: (mode: FoilSweep) => void
            $persist?: () => void
          },
        )
        ctx.store.setAnimateFoilThumbs(ctx.store.animateFoilThumbs === true)
        applyCoinQualityMigration(
          ctx.store as unknown as {
            coinQuality: CoinQuality
            setCoinQuality: (level: CoinQuality) => void
            $persist?: () => void
          },
        )
        applyMotionMigration(
          ctx.store as unknown as {
            motionLevel: MotionLevel
            reduceAnimations: boolean
            setMotionLevel: (level: MotionLevel) => void
            $persist: () => void
          },
        )
      },
    },
  },
)
